/**
 * Where a GroundTruth IO launch stands, read off the campaign's activity
 * messages, and where a campaign link should open.
 *
 * Only for choosing what to RENDER before the server answers. Which GT
 * campaign the record launched is decided by the server
 * (apps/app-backend/services/gtLiveState/gtLaunchIdentity.js — keep the two
 * rules in step), and the workspace defers to its answer once loaded:
 *   - a launch counts only when its gt-api create message completed with
 *     success === true; a failed result that recorded a campaign id does not
 *     (AC-02);
 *   - one that reported success WITH errors (an ad group that failed, a
 *     campaign it could not pause) is 'launched_partial', not a clean launch;
 *   - deleted messages are ignored, as the server ignores them.
 * The record's own status ('draft') is never consulted.
 */
import { api, getApiUrl } from '../../utils/apiConfig';

const GT_CREATE_SUB_ACTIONS = new Set(['create-campaign', 'create-campaign-from-json', 'create-campaign-from-pdf']);

const succeeded = (result) => result?.success === true || result?.success === 'true';

const resultOf = (message) => {
  const raw = message?.json_response;
  if (raw && typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw || null;
};

const newestFirst = (a, b) => (
  new Date(b.message_timestamp || b.timestamp || 0) - new Date(a.message_timestamp || a.timestamp || 0));

/** 'launched' | 'launched_partial' | 'launching' | 'launch_failed' | null (not a GT IO launch record). */
export function gtLaunchStateFromMessages(campaign, messages) {
  if (String(campaign?.destination || '').toLowerCase() !== 'groundtruth') return null;
  const launches = (messages || [])
    .filter((m) => m?.action === 'gt-api' && GT_CREATE_SUB_ACTIONS.has(m.sub_action) && !m.deleted_at)
    .sort(newestFirst);
  if (!launches.length) return null;
  const success = launches.find((m) => m.status === 'completed' && succeeded(resultOf(m)));
  if (success) {
    const errors = resultOf(success)?.errors;
    return Array.isArray(errors) && errors.length ? 'launched_partial' : 'launched';
  }
  return launches[0].status === 'processing' ? 'launching' : 'launch_failed';
}

/** A launched GT campaign exists (cleanly or not): its live state can be read. */
export const isGtLaunched = (state) => state === 'launched' || state === 'launched_partial';

/**
 * The campaign id a review link should open as a launched GT workspace, or
 * null to stay put: the record itself once its GT launch succeeded, or the
 * launch that replaced a retired draft (AC-03). Any failure keeps the caller's
 * existing behaviour.
 */
export async function findGtLaunchedWorkspace({ campaignId, accountId, userId }) {
  if (!campaignId || !accountId) return null;
  try {
    // Bounded: every CLW hydration waits on this, GT or not.
    const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-workspace`), {
      params: { accountId, userId }, timeout: 5000,
    });
    const body = res.data || {};
    if (body.replacedDraft && body.campaignId) return body.campaignId;
    return body.identity?.state === 'launched' ? body.campaignId : null;
  } catch {
    return null;
  }
}
